import {
    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR,
    WIDGET_VERSION,
    WIDGET_VERSION_DATA_ATTR,
} from "@/constants";
import { REWARDS_WIDGET_NAMES } from "@/constants/widget-names";
import {
    RewardsLoyaltyInfoPageWidgetDefinition,
    RewardsLoyaltyInfoPageWidgetV2Definition,
} from "@apps/rewards/constants/definitions";
import type { RewardsWidgetInjectorConfig } from "@apps/rewards/types";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import {
    parseOptionalBooleanDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";

import RewardsLoyaltyInfoPageWidget from "./v1/RewardsLoyaltyInfoPageWidget";
import RewardsLoyaltyInfoPageWidgetV2 from "./v2/RewardsLoyaltyInfoPageWidgetV2";

const CUSTOM_REDEEM_LINK_DATA_ATTR = "gw-custom-redeem-link";
const CUSTOM_POINTS_IMG_URL_DATA_ATTR = "gw-custom-points-img-url";
const CUSTOM_REFERRAL_IMG_URL_DATA_ATTR = "gw-custom-referral-img-url";
const CUSTOM_VIP_TIERS_IMG_URL_DATA_ATTR = "gw-custom-vip-tiers-img-url";

class RewardsLoyaltyInfoPageWidgetInjector {
    constructor(private readonly config: RewardsWidgetInjectorConfig) {}

    start(): void {
        this.config.logger.debug("RewardsLoyaltyInfoPageWidgetInjector start", {
            placeholderSelector: this.config.placeholderSelector,
        });

        const targets$ = this.config.mutationObserverInjector.register({
            targetSelector: this.config.placeholderSelector,
        });

        targets$.subscribe((placeholder) => {
            this.config.logger?.debug(
                "RewardsLoyaltyInfoPageWidgetInjector found placeholder",
                {
                    placeholder,
                }
            );

            void (async () => {
                const widgetVersion = parseStringDataAttr<WIDGET_VERSION>(
                    placeholder,
                    WIDGET_VERSION_DATA_ATTR
                );

                const isWidgetShadowRootEnabled = parseOptionalBooleanDataAttr(
                    placeholder,
                    WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                );

                const customRedeemLinkUrl = parseStringDataAttr(
                    placeholder,
                    CUSTOM_REDEEM_LINK_DATA_ATTR
                );

                const customPointsImgUrl = parseStringDataAttr(
                    placeholder,
                    CUSTOM_POINTS_IMG_URL_DATA_ATTR
                );

                const customReferralImgUrl = parseStringDataAttr(
                    placeholder,
                    CUSTOM_REFERRAL_IMG_URL_DATA_ATTR
                );

                const customVipTiersImgUrl = parseStringDataAttr(
                    placeholder,
                    CUSTOM_VIP_TIERS_IMG_URL_DATA_ATTR
                );

                const commonWidgetConfig = {
                    target: placeholder,
                    widgetVersion,
                    customRedeemLinkUrl,
                    customPointsImgUrl,
                    isWidgetShadowRootEnabled,
                };

                let widget:
                    | RewardsLoyaltyInfoPageWidget
                    | RewardsLoyaltyInfoPageWidgetV2;
                let widgetName: REWARDS_WIDGET_NAMES;

                if (widgetVersion === WIDGET_VERSION.V1) {
                    const RewardsLoyaltyInfoPageWidget =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsLoyaltyInfoPageWidgetDefinition,
                            true
                        );
                    widget = new RewardsLoyaltyInfoPageWidget(
                        {
                            ...commonWidgetConfig,
                            customReferralImgUrl,
                            customVipTiersImgUrl,
                        },
                        this.config.logger,
                        this.config.dynamicImportManager
                    );
                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_LOYALTY_INFO_PAGE_WIDGET;
                } else {
                    const RewardsLoyaltyInfoPageWidgetV2 =
                        await this.config.dynamicImportManager.importDefault(
                            RewardsLoyaltyInfoPageWidgetV2Definition,
                            true
                        );
                    widget = new RewardsLoyaltyInfoPageWidgetV2(
                        commonWidgetConfig,
                        this.config.logger,
                        this.config.dynamicImportManager
                    );
                    widgetName =
                        REWARDS_WIDGET_NAMES.REWARDS_LOYALTY_INFO_PAGE_WIDGET_V2;
                }

                await widget.init();

                this.config.eventsCollector.pushEvent(
                    this.config.eventsFactory.createWidgetInjected(widgetName)
                );

                dispatchSdkEvent(new WidgetInjectedSdkEvent(widgetName));
            })();
        });
    }
}

export default RewardsLoyaltyInfoPageWidgetInjector;
